import React, { useEffect, useContext, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { 
  GraduationCap, 
  ArrowRight, 
  Search, 
  BookOpen, 
  Mail, 
  Award, 
  Sparkles, 
  Users, 
  FileText, 
  X,
  Compass,
  Bookmark
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { residentialFacultyList } from '../../ProData';
import { ProductContext } from '../../ProductProvider';

export default function ResidentialFaculty() {
  const { setFacultyDetails } = useContext(ProductContext);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleCVRoutes = (e, item) => {
    e.preventDefault();
    setFacultyDetails(item);
    navigate(`/residentialfaculty/cv/${item.id}`);
  };

  // Helper to categorize faculty
  const getFacultyGroup = (item) => {
    const title = (item.title || '').toLowerCase();
    const cat = (item.category || '').toLowerCase();
    
    if (title.includes('principal') || title.includes('registrar') || title.includes('director') || title.includes('editor')) {
      return 'leadership';
    }
    if (cat.includes('systematic') || cat.includes('dialogue')) {
      return 'systematics';
    }
    if (cat.includes('scripture')) {
      return 'scripture';
    }
    if (cat.includes('moral') || title.includes('fieldwork')) {
      return 'moral_pastoral';
    }
    if (cat.includes('counsel') || title.includes('counsel')) {
      return 'counseling';
    }
    return 'other';
  };

  // Filter categories
  const categories = [
    { id: 'all', label: 'All Faculty', count: residentialFacultyList.length },
    { 
      id: 'leadership', 
      label: 'Leadership & Admin', 
      count: residentialFacultyList.filter(f => getFacultyGroup(f) === 'leadership').length 
    },
    { 
      id: 'systematics', 
      label: 'Systematic Theology', 
      count: residentialFacultyList.filter(f => (f.category || '').toLowerCase().includes('systematic') || getFacultyGroup(f) === 'systematics').length 
    },
    { 
      id: 'scripture', 
      label: 'Sacred Scripture', 
      count: residentialFacultyList.filter(f => (f.category || '').toLowerCase().includes('scripture')).length 
    },
    { 
      id: 'moral_pastoral', 
      label: 'Moral & Pastoral', 
      count: residentialFacultyList.filter(f => (f.category || '').toLowerCase().includes('moral') || (f.title || '').toLowerCase().includes('fieldwork')).length 
    },
    { 
      id: 'counseling', 
      label: 'Counseling & Formation', 
      count: residentialFacultyList.filter(f => (f.category || '').toLowerCase().includes('counsel')).length 
    }
  ];

  // Memoized filtered list
  const filteredFaculty = useMemo(() => {
    return residentialFacultyList.filter((item) => {
      // Category match
      if (selectedCategory !== 'all') {
        const group = getFacultyGroup(item);
        if (selectedCategory === 'leadership' && group !== 'leadership') return false;
        if (selectedCategory === 'systematics' && group !== 'systematics' && !(item.category || '').toLowerCase().includes('systematic')) return false;
        if (selectedCategory === 'scripture' && group !== 'scripture' && !(item.category || '').toLowerCase().includes('scripture')) return false;
        if (selectedCategory === 'moral_pastoral' && group !== 'moral_pastoral' && !(item.category || '').toLowerCase().includes('moral') && !(item.title || '').toLowerCase().includes('fieldwork')) return false;
        if (selectedCategory === 'counseling' && group !== 'counseling' && !(item.category || '').toLowerCase().includes('counsel')) return false;
      }

      // Search query match
      if (searchQuery.trim() !== '') {
        const q = searchQuery.toLowerCase().trim();
        const nameMatch = (item.name || '').toLowerCase().includes(q);
        const titleMatch = (item.title || '').toLowerCase().includes(q);
        const catMatch = (item.category || '').toLowerCase().includes(q);
        const emailMatch = (item.emailID || '').toLowerCase().includes(q);
        const provinceMatch = (item.province || '').toLowerCase().includes(q);
        return nameMatch || titleMatch || catMatch || emailMatch || provinceMatch;
      }

      return true;
    });
  }, [searchQuery, selectedCategory]);

  return (
    <div className="vj-faculty-page py-5" style={{ background: '#f8fafc', minHeight: '100vh' }}>
      <Helmet>
        <title>Residential Faculty - Vidyajyoti College of Theology</title>
        <meta name="description" content="Meet the resident professors, scholars, and spiritual mentors at Vidyajyoti College of Theology, Delhi." />
        <meta name="keywords" content="Theology faculty, Jesuit professors Delhi, Vidyajyoti resident faculty, Catholic theology scholars" />
        <link rel="canonical" href="/about/residentialFaculty" />
      </Helmet>

      <div className="container">
        {/* Header */}
        <div className="text-center mx-auto pb-4" style={{ maxWidth: '800px' }}>
          <div 
            className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-3" 
            style={{ background: 'rgba(121, 3, 29, 0.08)', color: '#79031d', fontSize: '0.85rem', fontWeight: 600, border: '1px solid rgba(121, 3, 29, 0.2)' }}
          >
            <Sparkles size={14} /> SCHOLARS, RESEARCHERS &amp; MENTORS
          </div>
          <h1 className="display-6 fw-bold mb-3" style={{ color: '#1a2b56', fontFamily: "'Outfit', sans-serif" }}>
            Residential Faculty
          </h1>
          <p className="lead text-muted" style={{ fontSize: '1.05rem', lineHeight: '1.7' }}>
            Our esteemed resident professors bring decades of theological scholarship, intercultural immersion, pastoral praxis, and Ignatian mentoring to form leaders for the Church and society.
          </p>
        </div>

        {/* Academic Key Highlights Bar */}
        <div className="card border-0 rounded-4 shadow-sm p-4 bg-white mb-5">
          <div className="row g-3 text-center">
            <div className="col-6 col-md-3 border-end">
              <div className="d-flex flex-column align-items-center">
                <div className="d-flex align-items-center gap-1 mb-1">
                  <Users size={18} className="text-primary" />
                  <span className="fw-bold fs-4" style={{ color: '#1a2b56' }}>13</span>
                </div>
                <small className="text-muted fw-semibold">Resident Scholars</small>
              </div>
            </div>
            <div className="col-6 col-md-3 border-end">
              <div className="d-flex flex-column align-items-center">
                <div className="d-flex align-items-center gap-1 mb-1">
                  <BookOpen size={18} style={{ color: '#c69214' }} />
                  <span className="fw-bold fs-4" style={{ color: '#1a2b56' }}>4+</span>
                </div>
                <small className="text-muted fw-semibold">Theological Disciplines</small>
              </div>
            </div>
            <div className="col-6 col-md-3 border-end">
              <div className="d-flex flex-column align-items-center">
                <div className="d-flex align-items-center gap-1 mb-1">
                  <Award size={18} style={{ color: '#79031d' }} />
                  <span className="fw-bold fs-4" style={{ color: '#1a2b56' }}>100%</span>
                </div>
                <small className="text-muted fw-semibold">Pontifical Faculty</small>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="d-flex flex-column align-items-center">
                <div className="d-flex align-items-center gap-1 mb-1">
                  <FileText size={18} className="text-success" />
                  <span className="fw-bold fs-4" style={{ color: '#1a2b56' }}>100+</span>
                </div>
                <small className="text-muted fw-semibold">Books &amp; Articles</small>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Filter Controls */}
        <div className="mb-4">
          <div className="row g-3 align-items-center justify-content-between">
            {/* Search Input */}
            <div className="col-lg-5 col-md-6">
              <div className="position-relative">
                <Search 
                  size={18} 
                  className="position-absolute top-50 translate-middle-y text-muted" 
                  style={{ left: '16px' }} 
                />
                <input
                  type="text"
                  className="form-control rounded-pill ps-5 pe-5 py-2 shadow-sm border"
                  placeholder="Search by professor name, discipline, or title..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ fontSize: '0.95rem', borderColor: 'rgba(26,43,86,0.15)' }}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="btn btn-link position-absolute top-50 translate-middle-y text-muted p-0"
                    style={{ right: '16px' }}
                    onClick={() => setSearchQuery('')}
                    title="Clear search"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>

            {/* Results count text */}
            <div className="col-lg-7 col-md-6 text-md-end text-muted small">
              Showing <strong className="text-dark">{filteredFaculty.length}</strong> of <strong>{residentialFacultyList.length}</strong> resident faculty members
            </div>
          </div>

          {/* Category Filter Tabs */}
          <div className="d-flex flex-wrap gap-2 mt-3">
            {categories.map((cat) => {
              const isActive = selectedCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`btn rounded-pill px-3 py-1.5 small fw-semibold transition-all ${
                    isActive 
                      ? 'btn-dark text-white shadow-sm' 
                      : 'btn-outline-secondary bg-white text-secondary'
                  }`}
                  style={{
                    backgroundColor: isActive ? '#1a2b56' : '#ffffff',
                    borderColor: isActive ? '#1a2b56' : 'rgba(26,43,86,0.15)',
                    color: isActive ? '#ffffff' : '#475569',
                    fontSize: '0.85rem'
                  }}
                >
                  {cat.label} ({cat.count})
                </button>
              );
            })}
          </div>
        </div>

        {/* Faculty Grid */}
        {filteredFaculty.length > 0 ? (
          <div className="row g-4">
            <AnimatePresence>
              {filteredFaculty.map((item, index) => {
                const isLeadership = getFacultyGroup(item) === 'leadership';
                const qualsCount = (item.academicQualification || []).length;
                const booksCount = (item.booksPublished || []).length;
                const articlesCount = (item.researchArticles || []).length;

                return (
                  <motion.div
                    key={item.id || index}
                    className="col-lg-4 col-md-6 d-flex"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ delay: index * 0.04 }}
                  >
                    <div
                      className="card h-100 w-100 border-0 shadow-sm rounded-4 p-4 text-center bg-white d-flex flex-column align-items-center justify-content-between"
                      style={{
                        borderTop: isLeadership ? '4px solid #79031d' : '4px solid #1a2b56',
                        transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-6px)';
                        e.currentTarget.style.boxShadow = '0 16px 36px rgba(15, 28, 63, 0.12)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 16px rgba(15, 28, 63, 0.05)';
                      }}
                    >
                      <div className="w-100 d-flex flex-column align-items-center">
                        {/* Circular Avatar with Gradient Ring */}
                        <div
                          className="rounded-circle p-1 mb-3 shadow-sm"
                          style={{
                            background: isLeadership 
                              ? 'linear-gradient(135deg, #79031d 0%, #c69214 100%)' 
                              : 'linear-gradient(135deg, #1a2b56 0%, #c69214 100%)',
                            width: '144px',
                            height: '144px',
                            flexShrink: 0
                          }}
                        >
                          <img
                            src={item.avatarSrc}
                            alt={item.name}
                            className="rounded-circle w-100 h-100"
                            style={{
                              objectFit: 'cover',
                              background: '#f1f5f9'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>

                        {/* Badges */}
                        <div className="d-flex flex-wrap gap-1.5 justify-content-center mb-2">
                          {item.title && (
                            <span
                              className="badge rounded-pill px-2.5 py-1"
                              style={{
                                background: isLeadership ? 'rgba(121, 3, 29, 0.1)' : 'rgba(26, 43, 86, 0.08)',
                                color: isLeadership ? '#79031d' : '#1a2b56',
                                fontSize: '0.75rem',
                                fontWeight: 600
                              }}
                            >
                              <Award size={12} className="me-1 inline-block" />
                              {item.title}
                            </span>
                          )}

                          {item.category && item.category !== item.title && (
                            <span
                              className="badge rounded-pill px-2.5 py-1 bg-light text-secondary border"
                              style={{ fontSize: '0.75rem', fontWeight: 500 }}
                            >
                              <BookOpen size={11} className="me-1 inline-block" />
                              {item.category}
                            </span>
                          )}
                        </div>

                        {/* Name */}
                        <h4 
                          className="fw-bold mb-1" 
                          style={{ color: '#1a2b56', fontSize: '1.18rem', fontFamily: "'Outfit', sans-serif" }}
                        >
                          {item.name}
                        </h4>

                        {/* Academic Statistics Mini Tags */}
                        <div className="d-flex flex-wrap justify-content-center gap-1 mt-2 mb-2">
                          {qualsCount > 0 && (
                            <span className="badge bg-light text-dark border px-2 py-1 small" style={{ fontSize: '0.72rem' }}>
                              🎓 {qualsCount} Degrees
                            </span>
                          )}
                          {booksCount > 0 && (
                            <span className="badge bg-light text-dark border px-2 py-1 small" style={{ fontSize: '0.72rem' }}>
                              📚 {booksCount} Book{booksCount > 1 ? 's' : ''}
                            </span>
                          )}
                          {articlesCount > 0 && (
                            <span className="badge bg-light text-dark border px-2 py-1 small" style={{ fontSize: '0.72rem' }}>
                              📝 {articlesCount} Articles
                            </span>
                          )}
                        </div>

                        {/* Direct Email touchpoint */}
                        {item.emailID && item.emailID.trim() !== '' && item.emailID !== '-' && (
                          <a
                            href={`mailto:${item.emailID}`}
                            className="text-muted small text-decoration-none d-inline-flex align-items-center gap-1 mt-1 mb-2 hover-burgundy"
                            style={{ fontSize: '0.82rem' }}
                            title={`Send email to ${item.name}`}
                          >
                            <Mail size={13} className="text-secondary" />
                            <span>{item.emailID}</span>
                          </a>
                        )}
                      </div>

                      {/* Card Action Button */}
                      <div className="w-100 pt-3 border-top mt-3">
                        <button
                          type="button"
                          className="vj-btn-outline w-100 justify-content-center d-flex align-items-center gap-2 py-2"
                          onClick={(e) => handleCVRoutes(e, item)}
                          style={{ fontSize: '0.88rem' }}
                        >
                          <span>View Full Profile &amp; CV</span>
                          <ArrowRight size={15} />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        ) : (
          /* Empty State */
          <div className="card border-0 rounded-4 shadow-sm p-5 text-center bg-white my-4">
            <div 
              className="rounded-circle d-inline-flex align-items-center justify-content-center mx-auto mb-3"
              style={{ width: '64px', height: '64px', background: 'rgba(26,43,86,0.06)', color: '#1a2b56' }}
            >
              <Search size={28} />
            </div>
            <h4 className="fw-bold mb-2" style={{ color: '#1a2b56' }}>No faculty found</h4>
            <p className="text-muted mb-4 mx-auto" style={{ maxWidth: '450px' }}>
              We couldn’t find any resident faculty matching &ldquo;<strong>{searchQuery}</strong>&rdquo; in this category.
            </p>
            <div>
              <button
                type="button"
                className="btn rounded-pill px-4 py-2 text-white fw-semibold"
                style={{ background: '#1a2b56' }}
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                }}
              >
                Reset Search &amp; Filters
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

